import { audit, workflow } from '../../api/endpoints';
import type { AuditEvent, ControlEvent } from '../../api/types';
import { useAsync } from '../../hooks';
import { Card, Column, DataTable, Empty, ErrorBox, fmtDate, JsonBlock, StatusBadge } from '../../components/ui';
import { useState } from 'react';
import type { CardData } from '../RequestCardPage';

export function EventsTab({ data }: { data: CardData }) {
  const events = useAsync<ControlEvent[]>(() => workflow.controlEvents({ request: data.request.id }), [data.request.id]);
  const auditEvents = useAsync<AuditEvent[]>(
    () => audit.events({ entity_id: data.request.id }).catch(() => audit.events({}).catch(() => [] as AuditEvent[])),
    [data.request.id],
  );
  const [actionErr, setActionErr] = useState<unknown>(null);

  const complete = async (id: string, revision?: number) => {
    setActionErr(null);
    try {
      await workflow.completeEvent(id, revision);
      events.reload();
    } catch (x) { setActionErr(x); }
  };

  const cols: Column<ControlEvent>[] = [
    { key: 'kind', title: 'Тип', render: (r) => r.kind ?? r.title ?? '—' },
    { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'due_at', title: 'Срок', render: (r) => fmtDate(r.due_at) },
    { key: 'cycle', title: 'Цикл', render: (r) => String(r.cycle ?? '—') },
    { key: 'completed_at', title: 'Завершено', render: (r) => fmtDate(r.completed_at) },
    {
      key: 'actions', title: '', render: (r) => (
        r.status === 'open' || r.status === 'in_progress' || r.status === 'overdue'
          ? <button className="btn small" onClick={() => void complete(r.id, r.revision)}>Завершить</button>
          : null
      ),
    },
  ];

  const auditCols: Column<AuditEvent>[] = [
    { key: 'created_at', title: 'Время', render: (r) => fmtDate(r.created_at) },
    { key: 'action', title: 'Действие', render: (r) => r.action ?? '—' },
    { key: 'entity_type', title: 'Сущность', render: (r) => r.entity_type ?? '—' },
    { key: 'actor', title: 'Актор', render: (r) => (r.actor != null ? String(r.actor) : '—') },
    { key: 'correlation_id', title: 'Correlation', render: (r) => r.correlation_id ?? '—' },
  ];

  return (
    <div>
      <Card title="Контрольные события" actions={<button className="btn" onClick={events.reload}>Обновить</button>}>
        <ErrorBox error={events.error} onRetry={events.reload} />
        <ErrorBox error={actionErr} />
        <DataTable columns={cols} rows={events.data ?? []} empty="Событий нет" />
      </Card>

      <Card title="Аудит (события системы)">
        <ErrorBox error={auditEvents.error} />
        {(auditEvents.data ?? []).length === 0
          ? <Empty text="События аудита недоступны или отсутствуют" />
          : <DataTable columns={auditCols} rows={auditEvents.data ?? []} />}
        {auditEvents.data && auditEvents.data.length > 0 && auditEvents.data[0].payload ? (
          <JsonBlock data={auditEvents.data[0].payload} />
        ) : null}
      </Card>
    </div>
  );
}
